import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layout/AdminLayout";

function fullUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null or testimonial object

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(() => { document.title = "Depoimentos — Admin"; load(); }, []);

  const handleSave = async () => {
    try {
      const payload = {
        name: editing.name, photo_url: editing.photo_url || "", text: editing.text,
        rating: editing.rating || 5, status: editing.status || "ativo"
      };
      if (editing.id) {
        await api.put(`/admin/testimonials/${editing.id}`, payload);
      } else {
        await api.post(`/admin/testimonials`, payload);
      }
      toast.success("Salvo");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir depoimento?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Excluído");
    load();
  };

  const toggleStatus = async (t) => {
    const s = t.status === "ativo" ? "inativo" : "ativo";
    await api.put(`/admin/testimonials/${t.id}`, { ...t, status: s });
    load();
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setEditing({ ...editing, photo_url: fullUrl(r.data.url) });
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-12 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow mb-2">Vozes da comunidade</div>
            <h1 className="font-serif text-4xl" style={{ color: "var(--text-primary)" }}>Depoimentos</h1>
          </div>
          <button onClick={() => setEditing({ name: "", text: "", photo_url: "", rating: 5, status: "ativo" })} className="btn-primary" data-testid="new-testimonial-btn">
            <Plus size={16} /> Novo depoimento
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {items.map((t) => (
            <div key={t.id} className="brand-card p-6" data-testid={`testimonial-${t.id}`}>
              <div className="flex items-start gap-4 mb-3">
                {t.photo_url && <img src={t.photo_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />}
                <div className="flex-1">
                  <div className="font-serif text-lg" style={{ color: "var(--text-primary)" }}>{t.name}</div>
                  <div className="text-xs" style={{ color: "var(--copper)" }}>{"★".repeat(t.rating || 5)}</div>
                </div>
                <button onClick={() => toggleStatus(t)} className="text-xs px-2 py-1 rounded-full" style={{ background: t.status === "ativo" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", color: t.status === "ativo" ? "#16a34a" : "#dc2626" }}>
                  {t.status}
                </button>
              </div>
              <p className="text-sm mb-4 line-clamp-3" style={{ color: "var(--text-secondary)" }}>{t.text}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...t })} className="btn-secondary flex-1" style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }} data-testid={`edit-testimonial-${t.id}`}><Edit size={14} /> Editar</button>
                <button onClick={() => handleDelete(t.id)} className="p-2 rounded-full border" style={{ borderColor: "var(--border)", color: "#dc2626" }} data-testid={`delete-testimonial-${t.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-serif text-2xl mb-5">{editing.id ? "Editar depoimento" : "Novo depoimento"}</h3>
              <div className="space-y-3">
                <input className="brand-input" placeholder="Nome" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="testimonial-name-input" />
                <textarea rows={4} className="brand-input" placeholder="Depoimento" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="testimonial-text-input" />
                <div className="flex items-center gap-3">
                  {editing.photo_url && <img src={editing.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />}
                  <label className="btn-secondary cursor-pointer flex-1" style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}>
                    <Upload size={14} /> Foto (opcional)
                    <input type="file" accept="image/*" hidden onChange={handlePhoto} />
                  </label>
                </div>
                <div className="flex gap-3">
                  <select className="brand-input" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: parseInt(e.target.value) })}>
                    {[5,4,3,2,1].map((n) => <option key={n} value={n}>{"★".repeat(n)}</option>)}
                  </select>
                  <select className="brand-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
                <button onClick={handleSave} className="btn-primary w-full" data-testid="save-testimonial">Salvar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
